import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, filter, take, tap } from 'rxjs';

import {
  RegisterToSyncMessage,
  SyncMessage,
  TargetWindow,
  WindowId
} from '@gfe/core/models/sync-data.model';
import { PopupWindows } from '@gfe/core/services/popup-windows/popup-windows.service';
import { WindowRef } from '@gfe/core/services/window-ref.service';
import { GALIA_POPUP_DATA_VISUALIZATION_URL } from '@gfe/core/tokens/galia-url.token';
import { isNil } from '@gfe/core/utils/shared-utils';

import { CommentsConfigActions } from '../comments-config/comments-config.actions';
import { selectCommentableEntityTypes } from '../comments-config/comments-config.selectors';
import * as DataVisualizationPopupActions from '../data-visualization-popup/data-visualization-popup.actions';
import { closePopup } from '../data-visualization-popup/data-visualization-popup.actions';
import * as DataVisualizationActions from '../data-visualization/data-visualization.actions';
import { selectDataVisualizationState } from '../data-visualization/data-visualization.selectors';
import * as DataspaceActions from '../dataspace/dataspace.actions';
import { selectDataspaceState } from '../dataspace/dataspace.selectors';
import * as PlanActions from '../plan/plan.actions';
import { selectPlanState } from '../plan/plan.selectors';

/**
 * This replicator is used to sync the data visualization state between the popup and the
 * main window All necesary actions should be put in the syncActions array to be
 * replicated.
 */
@Injectable({
  providedIn: 'root'
})
export class PopupDataVisualizationReplicator {
  static WINDOW_ID_PARAM = 'w_id';
  static WINDOW_TYPE_PARAM = 'w_type';
  static POPUP_ID_PARAM = 'p_id';

  private readonly openPopupIds = new Map<string, WindowId>();
  private readonly popups = inject(PopupWindows);

  private readonly galiaPopupDataVisualizationUrl = inject(
    GALIA_POPUP_DATA_VISUALIZATION_URL
  );

  private readonly popupSize = {
    width: 800,
    height: 600
  };

  private readonly syncActions = [
    DataVisualizationActions.setFilterOnAggregator.type,
    DataVisualizationActions.setActionFocus.type,
    DataVisualizationActions.setTimelineRows.type,
    DataVisualizationActions.setVisualizationLists.type,
    DataVisualizationActions.setGaliaRoutes.type,
    DataVisualizationActions.setGaliaMarkers.type,
    DataVisualizationActions.setDataVisualizationState.type,
    DataVisualizationActions.toggleCalendarVisualization.type,
    DataVisualizationActions.toggleListVisualization.type,
    DataVisualizationActions.toggleTimelineVisualization.type,
    DataVisualizationActions.setVisuComponentStates.type,
    DataVisualizationActions.setTimelineTimeStep.type,
    DataVisualizationActions.setTimelineModificationDescriptors.type,
    DataVisualizationActions.applyTimelineModification.type,
    DataVisualizationActions.timelineModificationOutcome.type,
    DataVisualizationPopupActions.setActiveVisualizationModules.type,
    DataVisualizationPopupActions.setFilteringState.type,
    PlanActions.setPlan.type,
    DataspaceActions.switchToDataspace.type,
    CommentsConfigActions.loadCommentableTypesSuccess.type
  ];

  windowId: WindowId = { id: crypto.randomUUID(), type: 'main' };
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly windowRef = inject(WindowRef);

  // BroadcastChannel is used to replicate the data visualization state between the popup and the main window
  private readonly channel = new BroadcastChannel('data-visu-replication');

  // BroadcastChannel is used to register the popup as a target. The main window will receice a message from the popup when it is opened and ready.
  private readonly registerChannel = new BroadcastChannel('register-data-visu-replication');

  // The parent window of the popup. Only used in the popup window.
  private parentWindowId: WindowId | null = null;

  /*
    The targets of the messages. For the main window it will be the popup window(s).
    For the popup window it will be the main window to make a two way sync.
  */
  private readonly targets: TargetWindow[] = [];

  constructor() {
    // Replicate events
    this.channel.onmessage = (event: MessageEvent<SyncMessage>) => {
      if (this.shouldProcess(event.data)) {
        this.dispatchMessage(event.data.action);
      }
    };

    // Only used to register or unregister the popup
    this.registerChannel.onmessage = (event: MessageEvent<RegisterToSyncMessage>) => {
      if (event.data.register && this.shouldRegister(event.data)) {
        this.sendInitialData(event.data.childWindow);
        this.targets.push(event.data.childWindow);
      } else if (!event.data.register && this.shouldRegister(event.data)) {
        this.unregisterChild(event.data.childWindow);
      }
    };

    // Close all popups when main window is closed or refreshed
    // Only add listener for main window, not popup windows
    if (this.windowId.type === 'main') {
      this.windowRef.nativeWindow.addEventListener('beforeunload', () => {
        this.closeAllPopups();
      });
    }
  }

  register(childWindowId: WindowId, parentId: WindowId): void {
    this.windowId = childWindowId;
    this.parentWindowId = parentId;
    // Make the parent receive the actions from the child making a two way sync
    this.targets.push(this.parentWindowId);
    // Request the parent to register the child as a target
    this.registerChannel.postMessage({
      parentWindow: parentId,
      childWindow: this.windowId,
      register: true
    });
  }

  unregister(childWindowId: WindowId): void {
    this.windowId = childWindowId;
    this.registerChannel.postMessage({
      parentWindow: this.parentWindowId,
      childWindow: this.windowId,
      register: true
    });
  }

  unregisterChild(childWindowId: WindowId): void {
    const targetIndex = this.targets.findIndex((target) => target.id == childWindowId.id);
    if (targetIndex >= 0) {
      this.targets.splice(targetIndex, 1);
    }
  }

  broadCast$ = createEffect(
    () =>
      this.actions$.pipe(
        filter((action) => this.syncActions.includes(action.type) && !action._fromSync),
        tap((action) => {
          const message: SyncMessage = {
            source: this.windowId,
            targets: this.targets,
            action: { ...action, _fromSync: true }
          };
          this.channel.postMessage(message);
        })
      ),
    { dispatch: false }
  );

  openPopup$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataVisualizationPopupActions.openPopup),
        tap(() => {
          this.createPopup('data-visu');
        })
      ),
    { dispatch: false }
  );

  closePopup$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DataVisualizationPopupActions.closePopup),
        tap(() => {
          this.closePopup();
        })
      ),
    { dispatch: false }
  );

  private dispatchMessage(action: Action): void {
    this.store.dispatch({ ...action });
  }

  private shouldRegister(registerMessage: RegisterToSyncMessage): boolean {
    const { parentWindow, childWindow } = registerMessage;
    return (
      parentWindow.id == this.windowId.id &&
      parentWindow.type == this.windowId.type &&
      childWindow.type == 'data-visualization-popup'
    );
  }

  private shouldProcess(message: SyncMessage): boolean {
    return message.targets.some((target) => {
      return target.id == this.windowId.id && target.type == this.windowId.type;
    });
  }

  private sendInitialData(childWindow: WindowId): void {
    this.sendSnapshot(
      childWindow,
      this.store.select(selectDataVisualizationState),
      (state) => DataVisualizationActions.setDataVisualizationState({ newState: state })
    );
    this.sendSnapshot(childWindow, this.store.select(selectPlanState), (state) =>
      isNil(state.plan) ? null : PlanActions.setPlan({ plan: state.plan })
    );
    this.sendSnapshot(childWindow, this.store.select(selectDataspaceState), (state) =>
      isNil(state.dataspace)
        ? null
        : DataspaceActions.switchToDataspace({
            dataspace: state.dataspace,
            availableEntities: state.availableEntities,
            availableTransformers: state.availableTransformers,
            transformersInfo: state.transformersInfo
          })
    );
    this.sendSnapshot(
      childWindow,
      this.store.select(selectCommentableEntityTypes),
      (entityTypes) =>
        entityTypes.length === 0
          ? null
          : CommentsConfigActions.loadCommentableTypesSuccess({ entityTypes })
    );
  }

  private sendSnapshot<T>(
    childWindow: WindowId,
    slice$: Observable<T>,
    toAction: (slice: T) => Action | null
  ): void {
    slice$
      .pipe(
        take(1),
        tap((slice) => {
          const action = toAction(slice);
          if (isNil(action)) {
            return;
          }
          const message: SyncMessage = {
            source: this.windowId,
            targets: [childWindow],
            action: { ...action, _fromSync: true }
          };
          this.channel.postMessage(message);
        })
      )
      .subscribe();
  }

  createPopup(popupName: string): void {
    const popupId = crypto.randomUUID();
    const params = [
      `${PopupDataVisualizationReplicator.WINDOW_ID_PARAM}=${this.windowId.id}`,
      `${PopupDataVisualizationReplicator.WINDOW_TYPE_PARAM}=${this.windowId.type}`,
      `${PopupDataVisualizationReplicator.POPUP_ID_PARAM}=${popupId}`
    ].join('&');

    const result = this.popups.open(
      popupName,
      `${this.galiaPopupDataVisualizationUrl}?${params}`,
      { features: `width=${this.popupSize.width},height=${this.popupSize.height}` }
    );
    if (result === 'focused') {
      return;
    }
    if (result === 'blocked') {
      // The browser refused the window, so the state must go back to "no popup" or the
      // main window would hide its own visualization in favour of a window that is not there.
      this.store.dispatch(closePopup());
      return;
    }

    this.openPopupIds.set(popupName, { id: popupId, type: 'data-visualization-popup' });
    this.popups.closed(popupName).subscribe(() => this.forget(popupName));
  }

  /**
   * Drops every trace of a popup that is gone, so the toolbox button can open a fresh one
   * and the main window shows its own visualization again.
   */
  private forget(popupName: string): void {
    const popupWindowId = this.openPopupIds.get(popupName);
    this.openPopupIds.delete(popupName);
    if (!isNil(popupWindowId)) {
      this.unregisterChild(popupWindowId);
    }
    this.store.dispatch(closePopup());
  }

  /**
   * Closes the visualization popups this window opened. Called when main window is closed or
   * refreshed (beforeunload event).
   */
  private closeAllPopups(): void {
    for (const popupName of this.openPopupIds.keys()) this.popups.close(popupName);
    this.openPopupIds.clear();

    try {
      this.channel.close();
      this.registerChannel.close();
    } catch (error) {
      console.warn('Failed to close broadcast channels:', error);
    }
  }

  private closePopup() {
    if (this.windowId.type === 'data-visualization-popup') {
      window.close();
    }
  }
}
